import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import Layout from './components/Layout/Layout';
import AddParticipant from './pages/AddParticipant';
import LandingPage from './pages/LandingPage';
import ParticipantAdditionalInformation from './pages/ParticipantAdditionalInformation';
import EditParticipant from './pages/EditParticipant';

import { useAuth0 } from "@auth0/auth0-react";
import LoggedInPage from './pages/LoggedInPage';
import RegisterPage from './pages/RegisterPage';
import axios, { AxiosRequestConfig } from 'axios';
import UserProfile from './pages/UserProfile';
import EditUser from './pages/EditUser';
import ManageUsers from './pages/ManageUsers';
import ManageUsersV1 from './pages/ManageUsersV1';
import CreateUser from './pages/CreateUser';
import ManagePrograms from './pages/ManagePrograms';
import ManageProgramsV1 from './pages/ManageProgramsV1';
import CreateProgram from './pages/CreateProgram';
import EditProgram from './pages/EditProgram';
import ManageProgramForms from './pages/ManageProgramForms';
import ParticipantPrograms from './pages/ParticipantPrograms';
import Tracking from './pages/Tracking';
import SignUp from './pages/SignUpPage';
import DeactivatedUser from './pages/DeactivatedUser';
import ManageBilling from './pages/ManageBilling';
import ChangePlan from './pages/ChangePlan';
import ConfirmPlanChange from './pages/ConfirmPlanChange';
import PlanPaymentDetails from './pages/PlanPaymentDetails';
import ManagePaymentDetails from './pages/ManagePaymentDetails';
import EditFormInstance from './pages/EditFormInstance';
import ViewFormInstance from './pages/ViewFormInstance';
import ParticipantTracking from './pages/ParticipantTracking';
import Unauthorized from './pages/errors/Unauthorized';
import Protect from './components/Layout/Protect';
import { useMe, MeProvider } from './hooks/useMe';
import Participant from './pages/Participant';
import Admin from './pages/Admin';
import ViewOrganization from './pages/ViewOrganization';
import EditOrganization from './pages/EditOrganization';
import ManageGoalLibrary from './pages/ManageGoalLibrary';
import ManageTermsOfService from './pages/ManageTermsOfService';
import TermsOfService from './pages/TermsOfService';
import ManageUsage from './pages/ManageUsage';
import ManageDataExports from './pages/ManageDataExports';
import ManageDataExportHistory from './pages/ManageDataExportHistory';
import Help from './pages/Help';
import AccessDisabled from './pages/errors/AccessDisabled';
import Loader from './components/UI/Loader';
import PlanCancelled from './pages/errors/PlanCancelled';
import TrialEnded from './pages/errors/TrialEnded';
import ParticipantNotes from './pages/ParticipantNotes';
import Unsubscribe from './pages/UnsubscribePage';
import ParticipantCasePlans from './pages/ParticipantCasePlans';
import ParticipantCasePlan from './pages/ParticipantCasePlan';
import { createContext } from 'react';
import { OrganizationProvider, useOrganization } from './hooks/useOrganization';
import ReportsV1 from './pages/ReportsV1';
import ManageForms from './pages/ManageForms';
import FormV2 from './pages/FormV2';
import SelectPlan from './pages/SelectPlan';
import ParticipantNote from './pages/ParticipantNote';
import ParticipantMergeSearch from './pages/ParticipantMergeSearch';
import ParticipantMergeCompare from './pages/ParticipantMergeCompare';
import ProcessDuplicateParticipants from './pages/ProcessDuplicateParticipants';
import ImportParticipants from './pages/ImportParticipants';
import PublicForm from './pages/PublicForm';
import PublicSubmissionFollowUp from './pages/PublicSubmissionFollowUp';
import ProcessPublicSubmissions from './pages/ProcessPublicSubmissions';
import BulkParticipantManagement from './pages/BulkParticipantManagement';
import BulkParticipantActionHistory from './pages/BulkParticipantActionHistory';
import { bulkParticipantManagementEnabled, dataExportsEnabled, goalLibraryEnabled, householdEditorEnabled, manageProgramsV2Enabled, manageUsersV2Enabled, participantDataImportEnabled, programEnrollmentImportEnabled, publicFormsEnabled, publicSubmissionFollowUpsEnabled, reportsDashboardEnabled, reportsUpgradeEnabled } from './utils/features';
import ParticipantPublicSubmissions from './pages/ParticipantPublicSubmissions';
import LandingPageV2 from './pages/LandingPageV2';
import EditHousehold from './pages/EditHousehold';
import { clearReturnLocation, getCurrentLocationPath } from './utils/locationReturnService';
import { setPendingLoginReturnTo } from './utils/auth0LoginReturnService';
import ReportBuilderPage from './pages/ReportBuilder';
import ReportDashboardPage from './pages/ReportDashboard';
import ReportLayout from './components/reports/ReportLayout';

const getAuthErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as { error?: unknown; code?: unknown };
  return typeof candidate.error === 'string'
    ? candidate.error
    : typeof candidate.code === 'string'
      ? candidate.code
      : undefined;
};

const requiresInteractiveLogin = (error: unknown): boolean => {
  const code = getAuthErrorCode(error);
  return code === 'login_required' ||
    code === 'invalid_grant' ||
    code === 'missing_refresh_token';
};

type RetriableRequestConfig = AxiosRequestConfig & { _authRetry?: boolean };
const MeContext = createContext<MeProvider>({
  loading: true,
  error: undefined,
  user: undefined,
  refresh: () => { }
});

const OrganizationContext = createContext<OrganizationProvider>({
  loading: true,
  error: undefined,
  value: undefined,
  refresh: () => { }
});

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const tokenRequest = useRef<{
    cacheMode: 'on' | 'off';
    promise: Promise<string | undefined>;
  } | null>(null);
  const loginRedirectStarted = useRef(false);
  const latestLocation = useRef(location);
  const [axiosReady, setAxiosReady] = useState(false);

  latestLocation.current = location;

  let isLoggedIn = isAuthenticated;

  useEffect(() => {
    const isUnauthenticatedRequest = (config: AxiosRequestConfig) =>
        latestLocation.current.pathname.startsWith("/forms/public") ||
        config.url?.indexOf(".s3.") !== -1 ||
        config.url?.indexOf("/emails/unsubscribe") !== -1 ||
        config.url?.indexOf("/forms/public/") !== -1;

    const beginInteractiveLogin = (error: unknown, requestUrl?: string) => {
      if (!requiresInteractiveLogin(error) || loginRedirectStarted.current) {
        return;
      }

      loginRedirectStarted.current = true;
      const returnTo = getCurrentLocationPath(latestLocation.current);
      setPendingLoginReturnTo(returnTo);
      void loginWithRedirect({ appState: { returnTo } }).catch((loginError) => {
        loginRedirectStarted.current = false;
        console.warn('Unable to start interactive login after access-token failure', {
          code: getAuthErrorCode(loginError) ?? 'unknown',
          requestUrl
        });
      });
    };

    const getApiAccessToken = (cacheMode: 'on' | 'off' = 'on') => {
      const activeRequest = tokenRequest.current;
      if (!activeRequest || (cacheMode === 'off' && activeRequest.cacheMode !== 'off')) {
        const request = getAccessTokenSilently({ cacheMode });
        const entry = { cacheMode, promise: request };
        tokenRequest.current = entry;
        request.finally(() => {
          if (tokenRequest.current === entry) {
            tokenRequest.current = null;
          }
        }).catch(() => {
          // The caller handles a rejected acquisition. Prevent an unhandled
          // rejection from the cleanup promise above.
        });
      }

      const currentRequest = tokenRequest.current;
      if (!currentRequest) {
        throw new Error('Unable to start Auth0 access-token request');
      }
      return currentRequest.promise;
    };

    const attachAccessToken = async (
      config: RetriableRequestConfig,
      cacheMode: 'on' | 'off' = 'on'
    ) => {
      const tokenData = await getApiAccessToken(cacheMode);

      if (!tokenData) {
        throw Object.assign(
          new Error('Auth0 did not return an access token'),
          { error: 'login_required' }
        );
      }

      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokenData}`;
    };

    const handleTokenError = (error: unknown, requestUrl?: string) => {
      console.warn('Failed to get access token in order to make API call', {
        code: getAuthErrorCode(error) ?? 'unknown',
        requestUrl
      });
      beginInteractiveLogin(error, requestUrl);
    };

    const requestHandle = axios.interceptors.request.use(async config => {
      if (!config.headers || isUnauthenticatedRequest(config)) {
        return config;
      }

      try {
        // Keep one token acquisition in flight. This prevents concurrent API calls
        // from attempting to exchange the same rotating refresh token.
        await attachAccessToken(config);
      } catch (e) {
        handleTokenError(e, config.url);
        throw e;
      }
      return config;
    }, error => error);

    const responseHandle = axios.interceptors.response.use(
      response => response,
      async error => {
        const config = error.config as RetriableRequestConfig | undefined;

        if (
          error.response?.status !== 401 ||
          !config ||
          config._authRetry ||
          isUnauthenticatedRequest(config)
        ) {
          return Promise.reject(error);
        }

        config._authRetry = true;
        try {
          // The API rejected a token that was considered usable locally. Force an
          // Auth0 renewal once, then replay the original request with that token.
          await attachAccessToken(config, 'off');
          return axios(config);
        } catch (refreshError) {
          handleTokenError(refreshError, config.url);
          return Promise.reject(error);
        }
      }
    );

    // Child pages start their own data-loading effects. Do not mount them until
    // these global interceptors can attach a bearer token and recover a stale one.
    setAxiosReady(true);

    return () => {
      axios.interceptors.request.eject(requestHandle);
      axios.interceptors.response.eject(responseHandle);
    };
  }, [getAccessTokenSilently, loginWithRedirect]);

  // this needs to run after the useEffect above so that the 
  // token is attached to future calls like the useMe below
  const me = useMe();
  const organization = useOrganization();

  const whiteListedMePaths = [
    '/register',
    '/login',
    '/logged-in',
    '/signup',
    '/unsubscribe',
    '/errors/unauthorized',
    '/errors/access-disabled',
    '/errors/trial-ended',
    '/errors/plan-cancelled',
    '/plan/select'
  ];

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!whiteListedMePaths.includes(location.pathname)) {
      if (!location.pathname.startsWith("/forms/public/")) {
        if (!isAuthenticated) {
          navigate('/login', {
            replace: true,
            state: { returnTo: getCurrentLocationPath(location) }
          });
        } else if (!me.loading) {
          if (!me.user || !me.user.organizationId) {
            navigate('/register');
          } else if (me.user.deactivated) {
            navigate('/users/deactivated');
          } else if (!me.user.organizationPlanTypeId) {
            navigate('/plan/select');
          }
        }
      }
    }
  }, [me, location, isLoading, isAuthenticated]);

  useEffect(() => {
    const shouldClearReturnLocation =
      location.pathname === '/home' ||
      location.pathname.startsWith('/tracking') ||
      location.pathname.startsWith('/reports') ||
      location.pathname.startsWith('/admin') ||
      location.pathname === '/user/profile';

    if (shouldClearReturnLocation) {
      clearReturnLocation();
    }
  }, [location.pathname]);

  const loggedInRoutes = <>
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/logged-in" element={<LoggedInPage />} />
    <Route path="/plan/select" element={<SelectPlan />} />
    <Route path="/" element={<Navigate to="/home" />} />
    <Route path="/user/profile" element={<UserProfile />} />
    <Route path="/users/:id/edit" element={<EditUser />} />
    <Route path="/users/deactivated" element={<DeactivatedUser />} />
    <Route path="/help" element={<Help />} />
    {publicFormsEnabled ? <Route path="/home" element={<LandingPageV2 />} /> : <Route path="/home" element={<LandingPage />} />}
    {publicFormsEnabled && <Route path="/submissions/public" element={<ProcessPublicSubmissions />} />}
    <Route path="/participants/duplicates" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ProcessDuplicateParticipants /></Protect>} />
    {bulkParticipantManagementEnabled && <Route path="/bulk-management/participants" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><BulkParticipantManagement /></Protect>} />}
    {bulkParticipantManagementEnabled && <Route path="/bulk-management/history" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><BulkParticipantActionHistory /></Protect>} />}
    {(participantDataImportEnabled || programEnrollmentImportEnabled) && <Route path="/import" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ImportParticipants /></Protect>} />}
    {participantDataImportEnabled && <Route path="/import/participants" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ImportParticipants /></Protect>} />}
    {programEnrollmentImportEnabled && <Route path="/import/program-enrollment" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ImportParticipants /></Protect>} />}
    {(participantDataImportEnabled || programEnrollmentImportEnabled) && <Route path="/participants/import" element={<Navigate to="/import" replace />} />}
    <Route path="/participants/add" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><AddParticipant /></Protect>} />
    <Route path="/participants/:id/edit" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><EditParticipant /></Protect>} />
    <Route path="/participants/:id" element={<Participant />}>
      <Route path="programs" element={<ParticipantPrograms />} />
      <Route path="profile-demographics" element={<ParticipantAdditionalInformation />} />
      <Route path="tracking" element={<ParticipantTracking />} />
      {publicSubmissionFollowUpsEnabled && <Route path="submissions" element={<ParticipantPublicSubmissions />} />}
      <Route path="notes" element={<ParticipantNotes />} />
      <Route path="plans" element={<ParticipantCasePlans />} />
      <Route path="plans/:casePlanId" element={<ParticipantCasePlan />} />
      <Route path="notes/:noteId" element={<ParticipantNote />} />
    </Route>
    {householdEditorEnabled && <Route path="/households/:id/edit" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><EditHousehold /></Protect>} />}
    <Route path="/tracking/:type" element={<Tracking />} />
    {reportsUpgradeEnabled ? (
      <Route path="/reports" element={<ReportLayout />}>
        <Route index element={<Navigate to="new" replace />} />
        <Route path="groups" element={<Navigate to="new" replace />} />
        <Route
          path="groups/:groupId"
          element={reportsDashboardEnabled ? <ReportDashboardPage /> : <Navigate to="/reports/new" replace />}
        />
        <Route path="new" element={<ReportBuilderPage />} />
        <Route path=":reportId" element={<ReportBuilderPage />} />
      </Route>
    ) : (
      <Route path="/reports" element={<ReportsV1 />} />
    )}
    <Route path="/forms/:formId/versions/:versionId/instances/:instanceId/view" element={<ViewFormInstance />} />      
    <Route path="/forms/:formId/versions/:versionId/instances/:instanceId/edit" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><EditFormInstance /></Protect>} />
    <Route path="tos" element={<TermsOfService />} />
    <Route path="/admin" element={<Protect behavior="redirect" user={me.user} roles={['Admin']}><Admin /></Protect>}>
      <Route path="forms" element={<ManageForms />} />
      <Route path="forms/:id/versions/:versionId" element={<FormV2 />} />
      <Route path="programs/:id/edit" element={<EditProgram />} />
      <Route path="programs/create" element={<CreateProgram />} />
      <Route path="programs" element={manageProgramsV2Enabled ? <ManagePrograms /> : <ManageProgramsV1 />} />
      <Route path="programs/:id/forms" element={<ManageProgramForms />} />
      <Route path="usage" element={<ManageUsage />} />
      {dataExportsEnabled && <Route path="data-exports" element={<ManageDataExports />} />}
      {dataExportsEnabled && <Route path="data-exports/history" element={<ManageDataExportHistory />} />}
      <Route path="users" element={manageProgramsV2Enabled && manageUsersV2Enabled ? <ManageUsers /> : <ManageUsersV1 />} />
      <Route path="users/create" element={<CreateUser />} />
      <Route path="billing" element={<ManageBilling />} />
      <Route path="tos" element={<ManageTermsOfService />} />
      <Route path="organization" element={<ViewOrganization />} />
      <Route path="organization/edit" element={<EditOrganization />} />
      {goalLibraryEnabled && <Route path="goals-library" element={<ManageGoalLibrary />} />}
      <Route path="billing/manage/payment-details" element={<ManagePaymentDetails />} />
      <Route path="billing/plan/change" element={<ChangePlan />} />
      <Route path="billing/plan/payment-details" element={<PlanPaymentDetails />} />
      <Route path="billing/plan/confirm" element={<ConfirmPlanChange />} />
      <Route path="" element={<Navigate to="forms"/>} />
    </Route>
    <Route path="/participants/merge/search" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ParticipantMergeSearch /></Protect>} />    
    <Route path="/participants/merge/:mergeId/compare" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ParticipantMergeCompare /></Protect>} />
    <Route path="/participants/merge/:mergeId/search" element={<Protect behavior="redirect" user={me.user} roles={['Admin', 'Contributor']}><ParticipantMergeSearch /></Protect>} />    
    <Route path="*" element={<Navigate to="/home" />} />
  </>;

  const notLoggedInRoutes = <>
    <Route path="/" element={<Navigate to="/login" />} />
    <Route path="*" element={<Navigate to="/login" />} />
    
  </>;

  if (isLoading || !axiosReady) {
    return (
      <MeContext.Provider value={me}>
        <OrganizationContext.Provider value={organization}>
          <Layout>
            <Loader />
          </Layout>
        </OrganizationContext.Provider>
      </MeContext.Provider>);
  }

  if (me.user?.organizationAccessDisabled || me.user?.organizationPlanCancelled || me.user?.organizationTrialEnded) {
    return (
      <MeContext.Provider value={me}>
        <OrganizationContext.Provider value={organization}>
          <Layout>
            <Routes>
              <Route path="/errors/unauthorized" element={<Unauthorized />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/logged-in" element={<LoggedInPage />} />
              <Route path="/admin" element={<Protect behavior="redirect" user={me.user} roles={['Admin']}><Admin /></Protect>}>
                <Route path="billing" element={<ManageBilling />} />
                <Route path="tos" element={<ManageTermsOfService />} />
                <Route path="organization" element={<ViewOrganization />} />
                <Route path="organization/edit" element={<EditOrganization />} />
                <Route path="usage" element={<ManageUsage />} />
                <Route path="users" element={manageProgramsV2Enabled && manageUsersV2Enabled ? <ManageUsers /> : <ManageUsersV1 />} />
                <Route path="users/create" element={<CreateUser />} />
                <Route path="billing/manage/payment-details" element={<ManagePaymentDetails />} />
                <Route path="billing/plan/change" element={<ChangePlan />} />
                <Route path="billing/plan/payment-details" element={<PlanPaymentDetails />} />
                <Route path="billing/plan/confirm" element={<ConfirmPlanChange />} />
                <Route path="" element={<Navigate to="organization" />} />
              </Route>
              {me.user?.organizationAccessDisabled && <>
                <Route path="/errors/access-disabled" element={<AccessDisabled />} />
                <Route path="/" element={<Navigate to="/errors/access-disabled" />} />
                <Route path="*" element={<Navigate to="/errors/access-disabled" />} />
              </>}
              {me.user?.organizationPlanCancelled && <>
                <Route path="/errors/plan-cancelled" element={<PlanCancelled />} />
                <Route path="/" element={<Navigate to="/errors/plan-cancelled" />} />
                <Route path="*" element={<Navigate to="/errors/plan-cancelled" />} />
              </>}
              {me.user?.organizationTrialEnded && <>
                <Route path="/errors/trial-ended" element={<TrialEnded />} />
                <Route path="/" element={<Navigate to="/errors/trial-ended" />} />
                <Route path="*" element={<Navigate to="/errors/trial-ended" />} />
              </>}
            </Routes>
          </Layout>
        </OrganizationContext.Provider>
      </MeContext.Provider>);
  }

  return (
    <MeContext.Provider value={me}>
      <OrganizationContext.Provider value={organization}>
        <Layout>
          <Routes>
            <Route path="/errors/unauthorized" element={<Unauthorized />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/forms/public/submissions/follow-up/:token" element={<PublicSubmissionFollowUp />} />
            <Route path="/forms/public/submissions/follow-up" element={<PublicSubmissionFollowUp />} />
            <Route path="/forms/public/:id" element={<PublicForm />} />
            {isLoggedIn && loggedInRoutes}
            {!isLoggedIn && notLoggedInRoutes}
          </Routes>
        </Layout>
      </OrganizationContext.Provider>
    </MeContext.Provider>
  );
}

export {
  App,
  MeContext,
  OrganizationContext
};
